import CT from '@marketplace-ai/tags';
import { useState } from 'react';
import { Button } from '~/components/buttons/Button/Button';
import { getContractListingTitle, getViewerSignSide, hasViewerSigned } from '../contractParties';
import { ContractDetail as ContractDetailType } from '../types';
import { ContractPartyHeader } from './ContractPartyHeader';
import { ContractSourceLinks } from './ContractSourceLinks';
import { ContractStatusBadge } from './ContractStatusBadge';
import { ContractTermsGrid } from './ContractTermsGrid';
import { ContractTimeline } from './ContractTimeline';
import { SignContractDialog } from './SignContractDialog';
import { TerminationDialog } from './TerminationDialog';

const DETAIL_TAGS = CT.employer.contracts.contractDetail;

interface Props {
  contract: ContractDetailType;
  currentEmployerId?: number;
  isProviderView?: boolean;
  // [MCV-1829] Called with the name the party typed once they confirm the dialog.
  onSign: (signerName: string, consentAccepted: boolean) => void;
  signing?: boolean;
  signError?: string | null;
  onRequestTermination: (reason: string) => void;
  onWithdrawTermination: () => void;
}

export const ContractDetail = ({
  contract,
  currentEmployerId,
  isProviderView = false,
  onSign,
  signing = false,
  signError = null,
  onRequestTermination,
  onWithdrawTermination,
}: Props) => {
  const [terminationOpen, setTerminationOpen] = useState(false);
  // [MCV-1829] The contract the sign dialog is open for, so selecting another
  // contract does not carry an open dialog over to it.
  const [signDialogContractId, setSignDialogContractId] = useState<number | null>(null);

  // [MCV-1727] Sign only appears for a party to the contract (an employer that is
  // the listing or contracted employer, or a provider on a JOB/SERVICE contract)
  // whose own side is still unsigned, so it disappears once they have signed and
  // the pending wait is on the other party.
  const viewer = { currentEmployerId, isProviderView };
  const canSign =
    contract.status === 'PENDING' &&
    getViewerSignSide(contract, viewer) !== null &&
    !hasViewerSigned(contract, viewer);
  // [MCV-1769] A pending termination request is shown to every party. Only the
  // requesting side can withdraw it, and while it is pending nobody can send
  // another. The request only means something on a contract that is still open.
  const isOpen = contract.status === 'PENDING' || contract.status === 'ACTIVE';
  const terminationRequestedAt = isOpen ? contract.terminationRequestedAt : null;
  const canTerminate = isOpen && !contract.terminationRequestedAt;
  const canWithdraw = terminationRequestedAt !== null && contract.terminationRequestedByViewerSide;
  // The request time is a moment, not a calendar date, so it reads in the viewer's
  // own time zone: a request sent at 6pm Pacific is dated that day, not the next
  // UTC day.
  const terminationRequestedOn = terminationRequestedAt
    ? new Date(terminationRequestedAt).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  return (
    <div
      className="bg-white rounded-md shadow-lg p-5 text-left max-sm:[overflow-wrap:anywhere]"
      aria-live="polite"
    >
      <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 mb-1">
        {!isProviderView && contract.listingType === 'MSAAS' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-primary-50 text-white">
            B2B
          </span>
        )}
        {!isProviderView && contract.listingType === 'JOB' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-grey-500 text-white">
            JOB
          </span>
        )}
        {!isProviderView && contract.listingType === 'SERVICE' && (
          <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-grey-500 text-white">
            SERVICE
          </span>
        )}
        <h2 className="text-sm font-bold text-grey-50 flex-1 max-sm:order-first max-sm:basis-full">
          {getContractListingTitle(contract) ?? 'Contract'}
        </h2>
        <ContractStatusBadge status={contract.status} cyTag={DETAIL_TAGS.status} />
      </div>

      <p className="text-xs text-grey-500 mb-3">{contract.contractNumber}</p>

      <ContractPartyHeader
        contract={contract}
        currentEmployerId={currentEmployerId}
        isProviderView={isProviderView}
      />

      <ContractTermsGrid contract={contract} isProviderView={isProviderView} />

      <ContractTimeline contract={contract} />

      <ContractSourceLinks contract={contract} />

      {(canSign || canTerminate || terminationRequestedAt) && (
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-grey-900">
          {canSign && (
            <Button
              colorVariant="primary"
              onClick={() => setSignDialogContractId(contract.id)}
              label="Sign Contract"
              type="button"
              cyTag={DETAIL_TAGS.signBtn}
            />
          )}
          {canTerminate && (
            <Button
              colorVariant="error"
              onClick={() => setTerminationOpen(true)}
              label="Request Termination"
              type="button"
              cyTag={DETAIL_TAGS.terminateBtn}
            />
          )}
          {terminationRequestedOn && (
            <p className="text-sm text-grey-500" data-cy={DETAIL_TAGS.terminationStatus}>
              {`Termination requested on ${terminationRequestedOn}. Support will review it.`}
            </p>
          )}
          {canWithdraw && (
            <Button
              colorVariant="secondary"
              onClick={onWithdrawTermination}
              label="Withdraw request"
              type="button"
              cyTag={DETAIL_TAGS.withdrawTerminationBtn}
            />
          )}
        </div>
      )}

      {/* [MCV-1829] Open only while the viewer can still sign, so it closes by itself once
          the signature is recorded (or found to be there already) and the pane reloads.
          Keyed so the typed name and consent are dropped when it is reopened. */}
      {signDialogContractId === contract.id && canSign && (
        <SignContractDialog
          key={contract.id}
          open
          contractLabel={getContractListingTitle(contract) ?? contract.contractNumber}
          signing={signing}
          error={signError}
          onClose={() => setSignDialogContractId(null)}
          onConfirm={onSign}
        />
      )}

      {/* Keyed so the typed reason is dropped once the request is recorded (or
          withdrawn) or another contract is opened, and kept when the request fails. */}
      <TerminationDialog
        key={`${contract.id}:${contract.terminationRequestedAt ?? ''}`}
        open={terminationOpen}
        onClose={() => setTerminationOpen(false)}
        onConfirm={onRequestTermination}
      />
    </div>
  );
};
